import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import useAxiosInstance from "../../../../lib/useAxiosInstance";
import BarChartSection from "./BarChartSection";
import ProgressChartSection from "./ProgressChartSection";
import PieChartSection from "./PieChartSection";
import { Button, Tooltip } from "antd";
import { AlignLeft, AlignRight, ChevronDown, ChevronUp } from "lucide-react";
import Summary from "./Summary";
import LegendSection from "./LegendSection";
import { motion, AnimatePresence } from "framer-motion";

const AGE_PIE_CONFIG_ID = "age-distribution";

const buildFilterParams = (filters) => ({
  constituency: filters?.constituency || undefined,
  village: filters?.village || undefined,
  booth: filters?.booth || undefined,
});

const TabOne = ({ survey, filters }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const surveyId = survey?._id || survey?.id;

  const [showSummary, setShowSummary] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true);
  const [selectedSummaryId, setSelectedSummaryId] = useState("");
  const [selectedBarConfigId, setSelectedBarConfigId] = useState("");
  const [selectedProgressConfigId, setSelectedProgressConfigId] = useState("");
  const [selectedPieConfigId, setSelectedPieConfigId] = useState(AGE_PIE_CONFIG_ID);

  // NEW: Selected Legend Config ID (for fetching)
  const [selectedLegendConfigId, setSelectedLegendConfigId] = useState("");

  // DELETE MODAL STATES
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: allConfigs = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigs", surveyId],
    enabled: !!surveyId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/allChartConfig/${surveyId}`
      );
      const configs = res?.data?.data || [];

      if (!selectedSummaryId) {
        const firstSummary = configs.find(
          (cfg) => cfg.chartType === "summary" || cfg.chart_type === "summary"
        );
        if (firstSummary) setSelectedSummaryId(firstSummary._id);
      }

      if (!selectedBarConfigId) {
        const firstBar = configs.find(
          (cfg) => cfg.chartType === "bar" || cfg.chart_type === "bar"
        );
        if (firstBar) setSelectedBarConfigId(firstBar._id);
      }

      if (!selectedProgressConfigId) {
        const firstProgress = configs.find(
          (cfg) => cfg.chartType === "progress" || cfg.chart_type === "progress"
        );
        if (firstProgress) setSelectedProgressConfigId(firstProgress._id);
      }

      if (!selectedPieConfigId) {
        const firstPie = configs.find(
          (cfg) => cfg.chartType === "pie" || cfg.chart_type === "pie"
        );
        setSelectedPieConfigId(firstPie ? firstPie._id : AGE_PIE_CONFIG_ID);
      }

      // NEW: Auto-select legend config
      if (!selectedLegendConfigId) {
        const firstLegend = configs.find(
          (cfg) => cfg.chartType === "legend" || cfg.chart_type === "legend"
        );
        if (firstLegend) setSelectedLegendConfigId(firstLegend._id);
      }

      return configs;
    },
  });

  // CONFIG GROUPING
  const barConfigs = allConfigs.filter(
    (cfg) => cfg.chartType === "bar" || cfg.chart_type === "bar"
  );
  const progressConfigs = allConfigs.filter(
    (cfg) => cfg.chartType === "progress" || cfg.chart_type === "progress"
  );
  const pieConfigsFromApi = allConfigs.filter(
    (cfg) => cfg.chartType === "pie" || cfg.chart_type === "pie"
  );

  const hasSummaryConfig = allConfigs.some(
    (cfg) => cfg.chartType === "summary" || cfg.chart_type === "summary"
  );

  const hasLegendConfig = allConfigs.some(
    (cfg) => cfg.chartType === "legend" || cfg.chart_type === "legend"
  );

  const pieConfigs = [
    { _id: AGE_PIE_CONFIG_ID, heading: "Age Distribution", chartType: "pie" },
    ...pieConfigsFromApi,
  ];

  const filterParams = buildFilterParams(filters);

  // FETCH SUMMARY
  const { data: summaryData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "summary", selectedSummaryId, filterParams],
    enabled: !!selectedSummaryId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${surveyId}/summary`,
        { params: filterParams }
      );
      return res?.data?.data || [];
    },
  });

  // FETCH BAR
  const { data: barChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "bar", selectedBarConfigId, filterParams],
    enabled: !!selectedBarConfigId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedBarConfigId}`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  // FETCH PROGRESS
  const { data: progressChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "progress", selectedProgressConfigId, filterParams],
    enabled: !!selectedProgressConfigId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedProgressConfigId}`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  // FETCH PIE
  const { data: pieChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "pie", selectedPieConfigId, filterParams],
    enabled: !!selectedPieConfigId && selectedPieConfigId !== AGE_PIE_CONFIG_ID,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedPieConfigId}`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  // FETCH AGE PIE
  const { data: agePieChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["ageDistribution", surveyId, filterParams],
    enabled: !!surveyId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${surveyId}/ageDistribution`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  const effectivePieChartData =
    selectedPieConfigId === AGE_PIE_CONFIG_ID ? agePieChartData : pieChartData;

  // NEW: FETCH LEGEND DATA
  const { data: legendData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "legend", selectedLegendConfigId, filterParams],
    enabled: !!selectedLegendConfigId && hasLegendConfig, 
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedLegendConfigId}/colorLegend?surveyType=${survey?.surveyType}`,
        { params: filterParams }
      );
      return res?.data?.data || [];
    },
  });  

  const handleLegendColorSave = async (textValue, color) => {
    try {
      await axiosInstance.post(
        `/api/surveyAnalytics/chartConfig/${selectedLegendConfigId}/saveColor`,
        {
          text_value: textValue,
          color,
          surveyId
        }
      );

      queryClient.invalidateQueries([
        "chartConfigData",
        "legend",
        selectedLegendConfigId,
      ]);
    } catch (err) {
      console.error("Error saving legend color:", err);
    }
  };

  const openDeleteModalForType = (chartType) => {
    let id = null;

    if (chartType === "bar") id = selectedBarConfigId;
    else if (chartType === "progress") id = selectedProgressConfigId;
    else if (chartType === "pie") {
      if (selectedPieConfigId === AGE_PIE_CONFIG_ID) return;
      id = selectedPieConfigId;
    } else if (chartType === "summary") id = selectedSummaryId;
    else if (chartType === "legend") id = selectedLegendConfigId;

    if (!id) return;

    const cfg = allConfigs.find((c) => c._id === id);

    setConfigToDelete({ id, type: chartType, heading: cfg?.heading });
    setDeleteModalOpen(true);
  };
  return (
    <>
      {/* Dashboard Toggle Button */}
      <div className="absolute top-[50%] right-4 z-[20]">
        <Tooltip title={showDashboard ? "Hide Dashboard" : "Show Dashboard"}>
          <Button
            type="default"
            className="bg-white text-black border-none rounded-xl shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center w-12 h-12"
            onClick={() => setShowDashboard(!showDashboard)}
            icon={showDashboard ? <AlignLeft size={25} /> : <AlignRight size={25} />}
          />
        </Tooltip>
      </div>
      {/* Top Right Charts */}
      <AnimatePresence>
        {showDashboard && 
          <motion.div 
            key="dashboard"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="absolute top-[12%] right-2 !p-2 card-wrapper !rounded-xl flex flex-col items-center w-[500px] max-h-[80vh] !overflow-y-auto scroll-smooth"
          >
              <div className="flex gap-3 justify-center items-center w-full relative">
                {barChartData.length!==0 && 
                <BarChartSection
                    configs={barConfigs}
                    selectedConfigId={selectedBarConfigId}
                    setSelectedConfigId={setSelectedBarConfigId}
                    chartData={barChartData}
                />
                }
                <div className="w-[2px] bg-[#123865] rounded-full h-[250px]" />

                {progressChartData?.length!==0 && 
                  <ProgressChartSection
                    configs={progressConfigs}
                    selectedConfigId={selectedProgressConfigId}
                    setSelectedConfigId={setSelectedProgressConfigId}
                    chartData={progressChartData}
                  />
                }

              </div>
                <div className="w-full h-[2px] bg-[#123865] my-2 rounded-full" />
              <div className="w-full flex justify-center">
                <PieChartSection
                  configs={pieConfigs}
                  selectedConfigId={selectedPieConfigId}
                  setSelectedConfigId={setSelectedPieConfigId}
                  chartData={effectivePieChartData}
                />
              </div>
          </motion.div>
        }
      </AnimatePresence>

      {/* SUMMARY BUTTON */}
      {hasSummaryConfig && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[30] flex text-center">
          <Tooltip title={showSummary ? "Hide Feedback" : "View Feedback Summary"}>
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={showSummary ? <ChevronDown size={22} /> : <ChevronUp size={22} />}
              className="shadow-xl bg-[#0b65c2] hover:bg-[#123865] transition-transform hover:scale-110 flex justify-center"
              onClick={() => setShowSummary(!showSummary)}
            />
          </Tooltip>
        </div>
      )}

      {/* SUMMARY VIEW */}
      {showSummary && hasSummaryConfig && (
        <Summary
          data={summaryData}
          onClose={() => setShowSummary(false)}
          surveyId={surveyId}
        />
      )}

      {hasLegendConfig && legendData?.length!==0 && (
        <div className="absolute bottom-[1%] left-4 z-[999] w-72">
          <LegendSection
            configs={allConfigs.filter(
              (cfg) => cfg.chartType === "legend" || cfg.chart_type === "legend"
            )}
            surveyId={surveyId}
            surveyType={survey?.surveyType}
            selectedConfigId={selectedLegendConfigId}
            setSelectedConfigId={setSelectedLegendConfigId}
            legendData={legendData}
            onDelete={() => openDeleteModalForType("legend")}
            onAddColor={(textValue, color) => handleLegendColorSave(textValue, color)}
          />
        </div>
      )}
    </>
  );
};

export default TabOne;
